import { useCallback, useEffect, useState } from 'react';
import { MultipleFormGroup } from './MultipleFormGroup';
import { SelectComponent } from '../Select';
import { cleanHtmlValue } from '@utils';
import { TextField, UserAutocomplete } from '@sharedComponents/DesignSystem/Inputs';
import RichTextEditorFormField from '@sharedComponents/RichTextEditor';
import _get from 'lodash/get';

export const MultipleFormGroupElement = (props) => {
  const {
    schema,
    form,
    handleChange,
    autocompleteOptions,
    autocompleteValue,
    handleOnChange,
    isUsersLoading,
    lang,
  } = props;
  const { components } = schema;
  const multipleItems = form[schema.key] || [];
  const [inputValue, setInputValue] = useState(form);

  useEffect(() => {
    setInputValue(form);
  }, [form]);

  const handleAdd = useCallback(() => {
    multipleItems.push({});
    handleChange([...multipleItems]);
  }, [handleChange, multipleItems]);

  const handleDelete = useCallback(
    (id) => {
      multipleItems.splice(id, 1);
      handleChange([...multipleItems]);
    },
    [handleChange, multipleItems]
  );

  const handleGroupChange = useCallback(
    (event, groupId, groupKey, option) => {
      const group = multipleItems.find((item, id) => groupId === id);
      group[groupKey] = option ? option : cleanHtmlValue(event.target.value);
      multipleItems.splice(groupId, 1, group);
      if (option === null) {
        const componentWithPrefilledItemKey = components.find(
          (item) => item.prefilledValuePath
        )?.key;
        if (componentWithPrefilledItemKey) {
          if (multipleItems[groupId][componentWithPrefilledItemKey]) {
            multipleItems[groupId][componentWithPrefilledItemKey] = null;
          }
        }
      } else if (option) {
        const componentWithPrefilledItem = components.find((item) => item.prefilledValuePath);
        if (componentWithPrefilledItem) {
          multipleItems[groupId][componentWithPrefilledItem.key] = _get(
            multipleItems[groupId],
            componentWithPrefilledItem.prefilledValuePath,
            null
          );
        }
      }
      handleChange([...multipleItems]);
    },
    [handleChange, multipleItems]
  );

  const handleReorderDnd = (list, startIndex, endIndex) => {
    const result = list;
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleOnDndEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = handleReorderDnd(multipleItems, result.source.index, result.destination.index);
    handleChange([...items]);
  };

  const handleChangeInput = useCallback(
    (event, index, key) => {
      const value = cleanHtmlValue(event.currentTarget.value);
      const newInputValue = inputValue;

      newInputValue[schema.key][index][key] = value;
      setInputValue(newInputValue);
      handleGroupChange(event, index, schema.key, false);
    },
    [handleGroupChange, inputValue, schema.key]
  );

  const handleChangeRichEditor = useCallback(
    (value, index, key) => {
      const newInputValue = inputValue;

      newInputValue[schema.key][index][key] = value;

      setInputValue(newInputValue);
      handleGroupChange(null, index, schema.key, true);
    },
    [handleGroupChange, inputValue, schema.key]
  );

  const renderElement = (component, index) => {
    const { type, key } = component;
    let value = component.defaultOption ? component.defaultOption.value : component.value;
    if (form[schema.key] && form[schema.key][index] && form[schema.key][index][key]) {
      value = form[schema.key][index][key];
    }

    switch (type) {
      case 'textfield': {
        if (
          inputValue[schema.key] &&
          inputValue[schema.key][index] &&
          inputValue[schema.key][index][key]
        ) {
          value = inputValue[schema.key][index][key];
        }

        return (
          <TextField
            schema={component}
            multiline={false}
            index={index}
            onChange={handleChangeInput}
            value={value}
          />
        );
      }
      case 'userAutocomplete': {
        return (
          <UserAutocomplete
            schema={component}
            handleChange={handleGroupChange}
            autocompleteOptions={autocompleteOptions}
            autocompleteValue={autocompleteValue}
            handleOnChange={handleOnChange}
            value={value}
            isMultiple={true}
            index={index}
            isUsersLoading={isUsersLoading}
            lang={lang}
          />
        );
      }
      case 'select': {
        return (
          <SelectComponent
            schema={component}
            handleChange={handleGroupChange}
            value={value}
            index={index}
            isMultiple={true}
          />
        );
      }
      case 'richTextEditor': {
        return (
          <RichTextEditorFormField
            value={value}
            onChange={handleChangeRichEditor}
            index={index}
            schema={component}
            isMultipleGroupElement={true}
            className={'input'}
            testId={'section-block-description'}
          />
        );
      }
      default: {
        return 'Component is not defined';
      }
    }
  };

  return (
    <MultipleFormGroup
      multipleItems={multipleItems}
      components={components}
      renderElement={renderElement}
      handleAdd={handleAdd}
      handleDelete={handleDelete}
      handleOnDndEnd={handleOnDndEnd}
    />
  );
};
